/**
 * Build a hierarchical structure from flattened NAICS data,
 * given a helper lookup for name, tier, and parent relationships.
 *
 * @param {Array} flatData - An array of objects, e.g.:
 *   [
 *     { code: '00000000', value: [1097400, 1096200, ...] },
 *     { code: '05000000', value: [952600, 951100, ...] },
 *     ...
 *   ]
 * @param {Object} helper - The lookup object, e.g.:
 *   {
 *     "00000000": { "name": "Total Nonfarm", "tier": 1, "parent": null },
 *     "05000000": { "name": "Total Private", "tier": 2, "parent": "00000000" },
 *     ...
 *   }
 * @returns {Array} - A hierarchical array of NAICS objects
 */
export function buildNAICSHierarchy(flatData, helper) {
    // Step 1: Create a map of all node objects keyed by code.
    //         Each node will store `id`, `industry`, `value`, and an empty `children` array.
    const nodeMap = {};
  
    flatData.forEach(item => {
      const code = item.code;
      const helperInfo = helper[code];
  
      // If for some reason the code is not found in helper, skip or handle as needed.
      if (!helperInfo) {
        // No helper entry found for code
        return;
      }
  
      nodeMap[code] = {
        id: code,
        industry: helperInfo.name,
        value: item.value,
        tier: helperInfo.tier, 
        children: []
      };
    });
  
    // Step 2: Link children to their parents.
    //         We'll also collect "root" nodes (those with no parent).
    const roots = [];
  
    // We'll iterate over the codes we actually added in nodeMap
    for (const code of Object.keys(nodeMap)) {
      const parentCode = helper[code]?.parent;
      if (parentCode) {
        // If the parent is in nodeMap, push this node as a child of its parent.
        if (nodeMap[parentCode]) {
          nodeMap[parentCode].children.push(nodeMap[code]);
        } else {
          // Handle missing parent scenario if needed
          // Parent code not found in nodeMap
        }
      } else {
        // This node has no parent, so it's a root.
        roots.push(nodeMap[code]);
      }
    }
  
    return roots;
  }